import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

// Same cryptic symbols from your old code
const crypticElements = [
  '⌬', '◈', '⌭', '⎔', '◑', '◐', '⌾', '⍜', '∰', '⎈',
  '0x1f4b9', '0x3a7d', '0xe29b',
  '[][>', '</>>', '>>[]',
  '∆∆', '◊◊', '##', '||',
  '//*', '*\\', '\\*', '*/',
];

const squarePositions = {
  0: { bottom: '2rem', right: '2rem', top: 'auto', left: 'auto', scale: 1 },
  1: { top: '2rem', right: '2rem', bottom: 'auto', left: 'auto', scale: 1.2 },
  2: { top: '2rem', left: '2rem', bottom: 'auto', right: 'auto', scale: 1 },
  3: { bottom: '2rem', left: '2rem', top: 'auto', right: 'auto', scale: 1.2 },
};

const LandingBanner = ({ onEnter }) => {
  // From old code
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [time, setTime] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // From new code
  const [squareStepCount, setSquareStepCount] = useState(0);
  const [squareRotation, setSquareRotation] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isAnimatingRef = useRef(false);
  const lastWheelTimeRef = useRef(0);
  const bannerRef = useRef(null);
  const didAutoEnterRef = useRef(false);

  // On mount, start timer + check mobile
  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setTime((t) => t + 1);
    }, 1000);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Wheel and touch logic for navigation
  useEffect(() => {
    const bannerEl = bannerRef.current;
    if (!bannerEl) return;

    // Common function to handle navigation trigger
    const triggerNavigation = () => {
      if (isAnimatingRef.current || isTransitioning) return;
      
      isAnimatingRef.current = true;
      const newStep = squareStepCount + 1;
      setSquareStepCount(newStep);
      setSquareRotation((prev) => prev + 90);

      setTimeout(() => {
        isAnimatingRef.current = false;
      }, 1500);
    };

    // Handle mouse wheel events
    const handleWheel = (e) => {
      const now = Date.now();
      if (now - lastWheelTimeRef.current < 1500 || isTransitioning) return;
      lastWheelTimeRef.current = now;

      e.preventDefault();
      e.stopPropagation();

      if (e.deltaY > 0) {
        triggerNavigation();
      }
    };

    // Handle touch events for mobile
    let touchStartY = 0;
    
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const now = Date.now();
      if (now - lastWheelTimeRef.current < 1500 || isTransitioning) return;
      
      // If no touch position available, exit
      if (!e.changedTouches || !e.changedTouches[0]) return;
      
      const touchEndY = e.changedTouches[0].clientY;
      const touchDiff = touchStartY - touchEndY;
      
      // If user swiped down by at least 50px
      if (touchDiff > 50) {
        lastWheelTimeRef.current = now;
        triggerNavigation();
      }
    };

    // Add event listeners
    bannerEl.addEventListener('wheel', handleWheel, { passive: false });
    bannerEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    bannerEl.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    // Clean up event listeners
    return () => {
      bannerEl.removeEventListener('wheel', handleWheel);
      bannerEl.removeEventListener('touchstart', handleTouchStart);
      bannerEl.removeEventListener('touchend', handleTouchEnd);
    };
  }, [squareStepCount, isTransitioning]);

  // Trigger onEnter after 2 steps
  useEffect(() => {
    if (squareStepCount >= 1 && !didAutoEnterRef.current) {
      didAutoEnterRef.current = true;
      setIsTransitioning(true);

      // Fade out and transition to domain cards
      setTimeout(() => {
        onEnter();
      }, 1000);
    }
  }, [squareStepCount, onEnter]);

  // Mouse move for old code’s background effect
  const handleMouseMove = (e) => {
    if (isMobile) return;
    const x = (e.clientX / window.innerWidth) * 100;
    const y = (e.clientY / window.innerHeight) * 100;
    setMousePosition({ x, y });
  };

  return (
    <motion.div
      ref={bannerRef}
      className="relative min-h-screen bg-black text-white flex flex-col items-center justify-center overflow-hidden"
      onMouseMove={handleMouseMove}
      animate={{ opacity: isTransitioning ? 0 : 1 }}
      transition={{ duration: 1 }}
    >
      {/* 
        1) Animated background gradient (old code) 
           with slight motion from either time or mouse
      */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-900 opacity-40 backdrop-blur-sm"
        style={{
          transform: isMobile
            ? `translate(${Math.sin(time * 0.05) * 3}px, ${
                Math.cos(time * 0.05) * 3
              }px)`
            : `translate(${mousePosition.x / 50}px, ${mousePosition.y / 50}px)`,
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
      />

      {/* 
        2) Cryptic symbols (old code) 
      */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(isMobile ? 10 : 20)].map((_, i) => {
          const randomElement =
            crypticElements[Math.floor(Math.random() * crypticElements.length)];
          const speed = 0.05 + Math.random() * 0.2;
          const delay = Math.random() * 10;
          const size = isMobile
            ? 6 + Math.random() * 10
            : 8 + Math.random() * 15;

          return (
            <div
              key={i}
              className="absolute text-gray-700 font-mono transition-all duration-1000"
              style={{
                top: `${(time * speed + delay) % 100}%`,
                left: `${Math.random() * 100}%`,
                transform: `rotate(${time * speed * 10}deg)`,
                fontSize: `${size}px`,
                opacity: 0.03 + Math.random() * 0.08,
              }}
            >
              {randomElement}
            </div>
          );
        })}
      </div>

      {/* 
        3) Subtle grid (old code) 
      */}
      <div className="absolute inset-0 grid grid-cols-4 md:grid-cols-8 grid-rows-4 md:grid-rows-8 opacity-10">
        {[...Array(isMobile ? 16 : 64)].map((_, i) => (
          <div key={i} className="border border-gray-800" />
        ))}
      </div>

      {/* 
        4) Random “binary” text columns (old code) 
      */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(isMobile ? 3 : 8)].map((_, i) => (
          <div
            key={i}
            className="absolute top-0 text-xs font-mono text-gray-700"
            style={{
              left: `${i * (isMobile ? 20 : 10)}%`,
              transform: `translateY(${(time * 50 + i * 100) % 1000}px)`,
              opacity: 0.05,
            }}
          >
            {Math.random().toString(2).substr(2, 8)}
          </div>
        ))}
      </div>

      {/* 
        5) Main text block 
      */}
      <div
        className={`transform transition-all duration-1000 z-10 px-4 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        <div className="relative">
          {/* Faint border layers */}
          <div className="absolute -inset-4 md:-inset-8 border border-gray-800 opacity-20 rotate-1" />
          <div className="absolute -inset-4 md:-inset-8 border border-gray-800 opacity-10 -rotate-1" />

          {/* Corner squares */}
          {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([x, y], i) => (
            <div
              key={i}
              className="absolute w-6 md:w-8 h-6 md:h-8 border-gray-700 opacity-30"
              style={{
                top: y < 0 ? '-0.75rem' : 'auto',
                bottom: y > 0 ? '-0.75rem' : 'auto',
                left: x < 0 ? '-0.75rem' : 'auto',
                right: x > 0 ? '-0.75rem' : 'auto',
                borderTop: y < 0 ? '1px solid' : 'none',
                borderBottom: y > 0 ? '1px solid' : 'none',
                borderLeft: x < 0 ? '1px solid' : 'none',
                borderRight: x > 0 ? '1px solid' : 'none',
              }}
            />
          ))}

          {/* "Powered by cryptonames.world" */}
          <h1 className="text-lg sm:text-2xl md:text-4xl font-bold text-center tracking-wider relative">
            <span className="block text-sm sm:text-base md:text-lg mb-2 md:mb-4 opacity-70">
              Powered by
            </span>
            <span className="bg-gradient-to-r from-white to-gray-500 text-transparent bg-clip-text hover:to-white transition-all duration-500">
              cryptonames.world
            </span>
          </h1>

          {/* Second "cryptonames.world" with difference blend */}
          <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
            <span
              className="text-lg sm:text-2xl md:text-4xl font-bold tracking-wider text-white mix-blend-difference"
              style={{
                transform: `translate(${
                  Math.sin(time) * (isMobile ? 0.5 : 1)
                }px, ${Math.cos(time) * (isMobile ? 0.5 : 1)}px)`,
              }}
            >
              cryptonames.world
            </span>
          </div>
        </div>
      </div>

      {/* 
        6) The “white square” from your new code 
           that moves on wheel and triggers onEnter 
      */}
      <motion.div
        animate={{
          ...squarePositions[squareStepCount % 4],
          rotate: squareRotation,
          opacity: isTransitioning ? 0 : 1,
        }}
        transition={{
          duration: 1.5,
          ease: [0.6, 0.01, -0.05, 0.95],
          opacity: { duration: 0.8 },
        }}
        style={{
          position: 'fixed',
          width: 25,
          height: 25,
          background: 'white',
          zIndex: 1000,
        }}
      />
    </motion.div>
  );
};

export default LandingBanner;